import { TConfig } from "./config.ts"

const searchParams = {
  /**
   * Read a query param. Values written via `searchParams.set` are
   * `JSON.stringify`'d on the way in, so they round-trip cleanly here.
   *
   * Values written by other code paths (notably react-router-dom's
   * `useSearchParams().set` in `useMupiSelectionQueryParams`) store raw
   * strings without JSON-encoding. When the user navigates between such
   * pages and one that reads via this helper, `JSON.parse` would throw on
   * the raw string and crash the whole page (the original symptom on the
   * campaign-overview page after coming from mupi-selection).
   *
   * Falling back to the raw string keeps simple string/number configs
   * working without forcing every other consumer to know about the
   * JSON-encoding convention.
   */
  get<K extends keyof TConfig>(key: K): TConfig[K] | undefined {
    // eslint-disable-next-line no-restricted-syntax
    const data = new URL(window.location.href).searchParams.get(key)
    if (data === null) return

    try {
      return JSON.parse(data) as TConfig[K]
    } catch {
      return data as unknown as TConfig[K]
    }
  },
  set<K extends keyof TConfig>(
    key: K,
    value: TConfig[K],
    variant: "replaceState" | "pushState" = "replaceState",
  ): void {
    const url = new URL(window.location.href)
    // eslint-disable-next-line no-restricted-syntax
    url.searchParams.set(key, JSON.stringify(value))
    window.history[variant]({}, "", url.toString())
  },
  push<K extends keyof TConfig>(key: K, value: TConfig[K]) {
    searchParams.set(key, value, "pushState")
  },
}

export default searchParams
